import { HyperliquidBaseClient } from './base-client';
import { PriceCalculator } from './price-calculator';
import { FeeService } from './utils/fee-service';
import { debugLog } from './utils/logger';
import { INTERNAL_BUILDER_CONFIG } from './constants';
import type { SigningAdapter } from '../adapters/base-adapter';
import type { BuilderConfig, InternalClientConfig } from './types';
import type { GenericAction } from '../types/actions';

/**
 * Simplified order request matching backend API structure
 */
export interface SimplifiedOrderRequest {
  /** Asset symbol (e.g., 'ETH', 'BTC') */
  symbol: string;
  /** Order side */
  side: 'buy' | 'sell';
  /** Order type */
  orderType: 'market' | 'limit';
  /** Quantity in coins */
  quantity: string;
  /** Optional: limit price (required for limit orders) */
  price?: string;
  /** Optional: reduce only flag */
  reduceOnly?: boolean;
  /** Optional: slippage for market orders (default: 0.05 = 5%) */
  slippage?: number;
  /** Optional: take profit configuration */
  takeProfit?: {
    triggerPrice: string;
    orderType?: 'market' | 'limit';
  };
  /** Optional: stop loss configuration */
  stopLoss?: {
    triggerPrice: string;
    orderType?: 'market' | 'limit';
  };
}

/**
 * Cancel order request
 */
export interface SimplifiedCancelRequest {
  symbol: string;
  orderId: string | number;
}

/**
 * Update leverage request
 */
export interface SimplifiedLeverageRequest {
  symbol: string;
  leverage: number;
  marginMode: 'cross' | 'isolated';
}

/**
 * TP/SL order request for existing positions
 * Uses grouping: 'positionTpsl' to tie orders to position
 */
export interface SimplifiedTpSlRequest {
  symbol: string;
  /** Position size (use absolute value) */
  size: string;
  /** True if closing a long position, false for short */
  isLong: boolean;
  /** Optional: take profit trigger price */
  takeProfit?: {
    triggerPrice: string;
    /** If provided, uses limit order, otherwise market */
    limitPrice?: string;
  };
  /** Optional: stop loss trigger price */
  stopLoss?: {
    triggerPrice: string;
    /** If provided, uses limit order, otherwise market */
    limitPrice?: string;
  };
}

/**
 * Position info for batch closing
 */
export interface SimplifiedPositionClose {
  symbol: string;
  /** Position size with sign (positive = long, negative = short) */
  size: string;
}

/**
 * Simplified Hyperliquid client with symbol-based API
 * Handles asset lookup and price calculation automatically
 */
export class SimplifiedHyperliquidClient extends HyperliquidBaseClient {
  private priceCalculator: PriceCalculator;
  private feeService?: FeeService;

  constructor(config: {
    signingAdapter: SigningAdapter;
    isTestnet?: boolean;
    builder?: boolean | BuilderConfig;
    environment?: 'local' | 'staging' | 'prod';
    debug?: boolean;
  }) {
    // Normalize builder config for base client
    const normalizedConfig: InternalClientConfig = {
      signingAdapter: config.signingAdapter,
      isTestnet: config.isTestnet,
      builder: config.builder ? (typeof config.builder === 'boolean' ? INTERNAL_BUILDER_CONFIG : config.builder) : undefined,
      debug: config.debug,
    };

    super(normalizedConfig);

    // Initialize price calculator using base class's assetManager
    this.priceCalculator = new PriceCalculator(
      this.assetManager,
      config.isTestnet
    );

    // Pre-warm caches in background to eliminate first-order latency
    // Asset metadata: fetched once, cached for 1 hour (already done in AssetMetadataManager constructor)
    // Prices: fetched every 5 seconds to keep cache hot
    this.priceCalculator.startBackgroundFetch(5000);

    // Initialize fee service if builder is enabled
    // Get wallet address from the signing adapter
    if (config.builder) {
      debugLog('[Simplified Client] Builder enabled, initializing fee service...');
      const walletAddress = config.signingAdapter.getWalletAddress();
      const environment = config.environment || 'prod';
      debugLog('[Simplified Client] Wallet address:', walletAddress, 'Environment:', environment);
      this.feeService = new FeeService(walletAddress, environment);
      // Start background fee refresh immediately
      this.feeService.start().catch(error => {
        debugLog('[Simplified Client] Fee service initialization error:', error);
      });
      debugLog('[Simplified Client] Dynamic fee service initialized for wallet:', walletAddress);
    } else {
      debugLog('[Simplified Client] Builder disabled, no fee service');
    }

    debugLog('[Simplified Client] Initialized with background cache warming');
  }

  /**
   * Override base class method to provide dynamic builder fee
   * This is called by buildOrderAction in base-client
   */
  protected getBuilderConfig(): BuilderConfig | undefined {
    if (!this.builderConfig) {
      debugLog('[Simplified Client] No builder config available');
      return undefined;
    }

    // If fee service is active, use dynamic fee
    if (this.feeService) {
      const currentFeeBps = this.feeService.getFeeBps();
      const dynamicFee = this.feeService.getFeeInTenthsOfBps();
      debugLog('[Simplified Client] Fee service active - Current fee:', currentFeeBps, 'bps =', dynamicFee, 'tenths of bps');
      return {
        address: this.builderConfig.address,
        fee: dynamicFee,
      };
    }

    // Otherwise use static fee from config
    debugLog('[Simplified Client] No fee service, using static fee:', this.builderConfig.fee);
    return this.builderConfig;
  }

  /**
   * Place an order using symbol-based parameters
   * Drop-in replacement for backend API calls
   */
  async placeSymbolOrder(request: SimplifiedOrderRequest) {
    debugLog('[Simplified Client] Placing order:', request);

    // Get asset metadata (single call - no redundant lookups)
    const asset = await this.assetManager.getAssetBySymbol(request.symbol);
    if (!asset) {
      throw new Error(`Asset "${request.symbol}" not found`);
    }
    const assetId = asset.universeIndex;
    const szDecimals = asset.szDecimals;

    // Calculate price for market orders
    let price = request.price;
    if (request.orderType === 'market') {
      if (!price) {
        price = await this.priceCalculator.calculateMarketPrice(
          request.symbol,
          request.side,
          request.slippage
        );
      }
      debugLog('[Simplified Client] Calculated market price:', price);
    } else if (request.orderType === 'limit' && !price) {
      throw new Error('Price is required for limit orders');
    }

    // Round price to valid Hyperliquid precision
    if (price) {
      const originalPrice = price;
      price = this.priceCalculator.roundToTickSize(price, szDecimals);
      if (originalPrice !== price) {
        debugLog(`[Simplified Client] Rounded price from ${originalPrice} to ${price}`);
      }
    }

    // Round size to valid Hyperliquid precision
    const originalSize = request.quantity;
    const roundedSize = this.priceCalculator.roundToSzDecimals(request.quantity, szDecimals);
    if (originalSize !== roundedSize) {
      debugLog(`[Simplified Client] Rounded size from ${originalSize} to ${roundedSize}`);
    }

    // Check if we have TP/SL to combine with main order
    const hasTPSL = request.takeProfit || request.stopLoss;

    if (hasTPSL) {
      // Combine main order + TP/SL in a single batch with normalTpsl grouping
      debugLog('[Simplified Client] Combining main order with TP/SL in single batch');

      // Build the action manually to support mixed order types (limit/market + trigger)
      const nonce = Date.now();
      interface ActionOrder {
        a: number;
        b: boolean;
        p: string;
        s: string;
        r: boolean;
        t: { limit?: { tif: string }; trigger?: { triggerPx: string; isMarket: boolean; tpsl: string } };
        c?: string;
      }
      const actionOrders: ActionOrder[] = [];

      // 1. Add main order (market or limit)
      actionOrders.push({
        a: assetId,
        b: request.side === 'buy',
        p: price!,
        s: roundedSize,
        r: request.reduceOnly ?? false,
        t:
          request.orderType === 'market'
            ? { limit: { tif: 'Ioc' } }
            : { limit: { tif: 'Gtc' } },
      });

      // 2. Add Stop Loss trigger order (if provided)
      if (request.stopLoss) {
        const slTriggerPrice = this.priceCalculator.roundToTickSize(
          request.stopLoss.triggerPrice,
          szDecimals
        );
        actionOrders.push({
          a: assetId,
          b: request.side === 'sell', // Opposite side to close
          p: slTriggerPrice,
          s: '0', // Size 0 means attach to parent position
          r: true,
          t: {
            trigger: {
              isMarket: request.stopLoss.orderType !== 'limit',
              triggerPx: slTriggerPrice,
              tpsl: 'sl',
            },
          },
        });
      }

      // 3. Add Take Profit trigger order (if provided)
      if (request.takeProfit) {
        const tpTriggerPrice = this.priceCalculator.roundToTickSize(
          request.takeProfit.triggerPrice,
          szDecimals
        );
        actionOrders.push({
          a: assetId,
          b: request.side === 'sell', // Opposite side to close
          p: tpTriggerPrice,
          s: '0', // Size 0 means attach to parent position
          r: true,
          t: {
            trigger: {
              isMarket: request.takeProfit.orderType !== 'limit',
              triggerPx: tpTriggerPrice,
              tpsl: 'tp',
            },
          },
        });
      }

      // Build action with normalTpsl grouping
      const action: GenericAction = {
        type: 'order',
        orders: actionOrders,
        grouping: 'normalTpsl',
      };

      // Add builder config with dynamic fee if provided
      const builderConfig = this.getBuilderConfig();
      debugLog('[Simplified Client] Builder config for order:', builderConfig);
      if (this.feeService) {
        debugLog('[Simplified Client] Fee service active, dynamic fee:', this.feeService.getFeeInTenthsOfBps());
      }
      if (builderConfig) {
        const builder = {
          b: builderConfig.address.toLowerCase(),
          f: builderConfig.fee,
        };
        action.builder = builder;
        debugLog('[Simplified Client] Applied builder fee to action:', builder.f);
      }

      // Sign and send directly
      return this.signAndSendL1Action(action, nonce);
    }

    // No TP/SL - place simple order as before
    if (request.orderType === 'limit') {
      return this.placeLimitOrder({
        asset: assetId,
        isBuy: request.side === 'buy',
        price: price!,
        size: roundedSize,
        reduceOnly: request.reduceOnly ?? false,
        timeInForce: 'Gtc',
      });
    } else {
      // Market order
      return this.placeMarketOrder({
        asset: assetId,
        isBuy: request.side === 'buy',
        price: price!,
        size: roundedSize,
        reduceOnly: request.reduceOnly ?? false,
      });
    }
  }

  /**
   * Cancel an order by symbol and order ID
   */
  async cancelSymbolOrder(request: SimplifiedCancelRequest) {
    const assetId = await this.assetManager.getAssetId(request.symbol);

    return this.cancelOrders({
      cancels: [
        {
          asset: assetId,
          orderId: typeof request.orderId === 'string'
            ? parseInt(request.orderId, 10)
            : request.orderId,
        },
      ],
    });
  }

  /**
   * Cancel multiple orders by providing symbol and order ID
   * More reliable than using empty cancels array or o: 0 pattern
   * Matches what Hyperliquid UI actually sends
   *
   * @param orders - Array of orders to cancel with symbol and orderId
   * @example
   * ```ts
   * await client.cancelAllSymbolOrders([
   *   { symbol: 'ETH', orderId: '123456' },
   *   { symbol: 'BTC', orderId: '789012' }
   * ]);
   * ```
   */
  async cancelAllSymbolOrders(orders: SimplifiedCancelRequest[]) {
    // Build cancellations array by converting symbols to asset IDs
    const cancellations = await Promise.all(
      orders.map(async (order) => {
        const assetId = await this.assetManager.getAssetId(order.symbol);
        return {
          asset: assetId,
          orderId: typeof order.orderId === 'string'
            ? parseInt(order.orderId, 10)
            : order.orderId,
        };
      })
    );

    // Use existing cancelOrders method
    return super.cancelOrders({ cancels: cancellations });
  }

  /**
   * Place TP/SL orders for an existing position
   * Uses grouping: 'positionTpsl' to tie orders to the position
   *
   * @example
   * ```ts
   * // Set both TP and SL for a long ETH position
   * await client.placeSymbolTpSl({
   *   symbol: 'ETH',
   *   size: '0.5',
   *   isLong: true,
   *   takeProfit: { triggerPrice: '4200' },
   *   stopLoss: { triggerPrice: '3800' }
   * });
   * ```
   */
  async placeSymbolTpSl(request: SimplifiedTpSlRequest) {
    debugLog('[Simplified Client] Placing TP/SL for position:', request);

    // Get asset ID and metadata
    const assetId = await this.assetManager.getAssetId(request.symbol);
    const asset = await this.assetManager.getAssetBySymbol(request.symbol);
    const szDecimals = asset?.szDecimals ?? 4;

    // Round trigger prices to valid precision
    const takeProfit = request.takeProfit ? {
      triggerPrice: this.priceCalculator.roundToTickSize(request.takeProfit.triggerPrice, szDecimals),
      limitPrice: request.takeProfit.limitPrice
        ? this.priceCalculator.roundToTickSize(request.takeProfit.limitPrice, szDecimals)
        : undefined,
    } : undefined;

    const stopLoss = request.stopLoss ? {
      triggerPrice: this.priceCalculator.roundToTickSize(request.stopLoss.triggerPrice, szDecimals),
      limitPrice: request.stopLoss.limitPrice
        ? this.priceCalculator.roundToTickSize(request.stopLoss.limitPrice, szDecimals)
        : undefined,
    } : undefined;

    debugLog('[Simplified Client] Rounded TP/SL prices:', { takeProfit, stopLoss });

    // Round size to valid Hyperliquid precision
    const roundedSize = this.priceCalculator.roundToSzDecimals(request.size, szDecimals);
    if (request.size !== roundedSize) {
      debugLog(`[Simplified Client] Rounded TP/SL size from ${request.size} to ${roundedSize}`);
    }

    // Place TP/SL order with positionTpsl grouping
    return this.placeTpSlOrder({
      asset: assetId,
      size: roundedSize,
      isLong: request.isLong,
      takeProfit,
      stopLoss,
    });
  }

  /**
   * Close multiple positions with market orders in a single batch
   * More efficient than placing individual orders
   *
   * @example
   * ```ts
   * // Close all positions
   * await client.batchClosePositions([
   *   { symbol: 'ETH', size: '0.5' },   // Long position (positive)
   *   { symbol: 'BTC', size: '-0.1' }   // Short position (negative)
   * ]);
   * ```
   */
  async batchClosePositions(positions: SimplifiedPositionClose[]) {
    debugLog('[Simplified Client] Batch closing positions:', positions);

    if (positions.length === 0) {
      throw new Error('No positions to close');
    }

    // Build orders array with asset IDs and prices
    const orders = await Promise.all(
      positions.map(async (position) => {
        const positionSize = parseFloat(position.size);
        const isBuy = positionSize < 0; // Short position needs buy to close
        const asset = await this.assetManager.getAssetBySymbol(position.symbol);
        const assetId = asset?.universeIndex;
        const szDecimals = asset?.szDecimals ?? 4;

        if (assetId === undefined) {
          throw new Error(`Asset not found for symbol: ${position.symbol}`);
        }

        // Calculate market price with slippage
        const marketPrice = await this.priceCalculator.calculateMarketPrice(
          position.symbol,
          isBuy ? 'buy' : 'sell',
          0.05 // 5% slippage
        );

        // Round price to valid tick size
        const roundedPrice = this.priceCalculator.roundToTickSize(marketPrice, szDecimals);

        // Round size to valid Hyperliquid precision
        const roundedSize = this.priceCalculator.roundToSzDecimals(
          Math.abs(positionSize).toString(),
          szDecimals
        );

        return {
          asset: assetId,
          isBuy,
          price: roundedPrice,
          size: roundedSize,
          reduceOnly: true,
          orderType: 'market' as const,
        };
      })
    );

    debugLog('[Simplified Client] Batch close orders:', orders);

    // Place all orders in a single batch
    return this.batchOrders({
      orders,
      grouping: 'na', // No grouping for batch closes
    });
  }

  /**
   * Update leverage and margin mode for a symbol
   */
  async updateSymbolLeverage(request: SimplifiedLeverageRequest) {
    const assetId = await this.assetManager.getAssetId(request.symbol);

    return super.updateLeverage({
      asset: assetId,
      isCross: request.marginMode === 'cross',
      leverage: request.leverage,
    });
  }

  /**
   * Get current price for a symbol
   */
  async getCurrentPrice(symbol: string): Promise<string> {
    return this.priceCalculator.getCurrentPrice(symbol);
  }

  /**
   * Get asset metadata for a symbol
   */
  async getAssetMetadata(symbol: string) {
    return this.assetManager.getAssetBySymbol(symbol);
  }

  /**
   * Prefetch and cache asset metadata (optional optimization)
   * Call this during app initialization to speed up first order
   */
  async prefetchMetadata(): Promise<void> {
    await this.assetManager.getUniverseAssets();
  }

  /**
   * Cleanup resources when shutting down the SDK
   * Stops background price fetching and closes WebSocket connection
   *
   * @example
   * ```ts
   * const client = new SimplifiedHyperliquidClient(config);
   * // ... use client ...
   * client.destroy(); // Call when done to cleanup
   * ```
   */
  destroy(): void {
    debugLog('[Simplified Client] Destroying client and cleaning up resources');
    this.priceCalculator.stopBackgroundFetch();
    if (this.feeService) {
      this.feeService.stop();
    }
    this.closeConnections().catch((error) => {
      debugLog('[Simplified Client] Error closing connections:', error);
    });
  }
}
